import { Plus } from 'lucide-react';
import { useLocation, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useAuthStore } from '../store/auth.store';
import { Button } from './Button';
import Modal from './Modal';
import NewPlanForm from './plans/NewPlanForm';
import { InviteMemberForm } from './team/InviteMemberForm';
import { customerQueryOptions } from '../lib/queries/customers';
import EndpointForm from '../components/storage/EndpointForm';

const routeTitles: Record<string, string> = {
  '/': 'All tenants',
  '/plans': 'Plans & Billing',
  '/team': 'Team & Access',
  '/audit-log': 'Audit Log',
  '/customers/new': 'New tenant',
  '/impersonate': 'Impersonate',
  '/s3-endpoints': 'S3 Endpoints',
};

function useCustomerDetailId(): string | null {
  const { pathname } = useLocation();
  const match = pathname.match(/^\/customers\/([^/]+)$/);
  const id = match?.[1];
  // exclude /customers/new
  return id && id !== 'new' ? id : null;
}

function CustomerBreadcrumb({ id }: { id: string }) {
  const { data: tenant } = useQuery(customerQueryOptions(id));
  return (
    <div className="flex items-center gap-2 font-medium md:text-[1.25rem]">
      <Link to="/" className="text-muted hover:text-primary transition-colors">
        All Tenants
      </Link>
      <span className="text-muted">›</span>
      <span className="text-primary">{tenant?.hostAccountId ?? '…'}</span>
    </div>
  );
}

export function Header() {
  const role = useAuthStore((s) => s.role);
  const { pathname } = useLocation();
  const customerId = useCustomerDetailId();

  const title = routeTitles[pathname] ?? 'Console';

  return (
    <Modal>
      <header className="border-primary/12 flex h-16 shrink-0 items-center justify-between border-b px-6">
        {customerId ? (
          <CustomerBreadcrumb id={customerId} />
        ) : (
          <p className="text-primary text-base font-medium md:text-[1.25rem]">
            {title}
          </p>
        )}

        <div className="flex items-center gap-3">
          <span className="text-xs text-[--color-ink-muted] capitalize">
            {role}
          </span>

          {pathname === '/plans' ? (
            <Modal.Open opens="add-plans">
              <Button variant="secondary">
                <Plus size={18} /> Add plan
              </Button>
            </Modal.Open>
          ) : pathname === '/team' ? (
            <Modal.Open opens="invite-member">
              <Button variant="secondary">
                <Plus size={18} /> Invite member
              </Button>
            </Modal.Open>
          ) : pathname === '/impersonate' ? (
            ''
          ) : pathname === '/s3-endpoints' ? (
            <Modal.Open opens="add-endpoint">
              <Button className="hidden! md:inline-flex!">
                <Plus size={18} /> Add Endpoint
              </Button>
            </Modal.Open>
          ) : (
            <>
              <Button className="hidden! md:inline-flex!">
                <Link
                  to="/customers/new"
                  search={{ step: 1 }}
                  className="flex items-center gap-1"
                >
                  <Plus size={18} /> New tenant
                </Link>
              </Button>
            </>
          )}
        </div>
      </header>

      <Modal.Window name="add-plans">
        <NewPlanForm />
      </Modal.Window>

      <Modal.Window name="invite-member">
        <InviteMemberForm />
      </Modal.Window>

      <Modal.Window name="add-endpoint">
        <EndpointForm />
      </Modal.Window>
    </Modal>
  );
}
